import { useLazyQuery, useMutation, useSubscription } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { onProjectUpdate } from '@ucc/graphql/project/notifications';
import { onServiceRequestNotification } from '@ucc/graphql/service-request/notifications';
import React, { useState } from 'react';
import {
    ApproveServiceRequestDocument,
    OpenServiceRequestsDocument,
    RejectServiceRequestDocument,
    type ServiceRequestFieldsFragment,
} from '~/gql/ucc/graphql';
import { useDataLoader } from '~/hooks/useDataLoader';
import { useFetchDataWithFilter } from '~/hooks/useFetchDataWithFilter';
import { withRenderers } from '~/utils/withRenderers';
import { tables } from '../meta/data/dashboard.schema';
import log from '~/log';
import { useNavigate } from 'react-router-dom';
import { usePermission, useViewer } from '~/hooks';

export const useServiceRequest = (variables = {}) => {
    const { canCreate, canEdit, canDelete } = usePermission('dashboard.contractInbox');
    const [serviceRequestId, setServiceRequestId] = useState<string>();
    const [filter, setFilter] = useState<{
        branchId?: number;
        sourceSystem?: string;
    }>({});
    const toast = useToast();
    const hasSubscriptionErrorRef = React.useRef(false);
    const [approveServiceRequestMutation] = useMutation(ApproveServiceRequestDocument);
    const [rejectServiceRequestMutation] = useMutation(RejectServiceRequestDocument);
    const navigate = useNavigate();
    const viewer = useViewer();

    const selectSourceSystem = (sourceSystem?: string | null) =>
        setFilter((prev) => ({ ...prev, sourceSystem: sourceSystem ?? undefined }));
    const selectBranch = (branchId?: number | null) =>
        setFilter((prev) => ({ ...prev, branchId: branchId ?? undefined }));

    // TODO: @jfe progress screen
    useSubscription(onProjectUpdate, {
        variables: { id: serviceRequestId! },
        skip: !serviceRequestId,
        onData({ data }) {
            const { message, notification, metadata } = data.data?.onProjectUpdate ?? {};
            console.log('Notifications', notification, message, metadata);

            if (message) {
                toast({
                    title: message,
                    status: notification === 'SetupFailed' ? 'error' : 'success',
                    duration: 2000,
                });
                if (notification === 'SetupSuccess') {
                    try {
                        const { navigateTo } = JSON.parse(metadata);
                        navigateTo && navigate(navigateTo);
                    } catch (error) {}
                }
            }
        },
    });

    const [load, { data, loading, refetch }] = useLazyQuery(OpenServiceRequestsDocument, {
        context: {},
        fetchPolicy: 'network-only',
    });

    const refreshServiceRequests = React.useCallback(async () => {
        try {
            await refetch();
        } catch (error) {
            log.error('serviceRequest.refetch.failed', error);
        }
    }, [refetch]);

    const handleServiceRequestNotification = React.useCallback(
        ({ data }: any) => {
            if (hasSubscriptionErrorRef.current) {
                hasSubscriptionErrorRef.current = false;
            }

            const message = data.data?.onServiceRequestNotification?.message;

            if (message) {
                toast({
                    title: message,
                    status: 'success',
                    duration: 2000,
                });
            }

            refreshServiceRequests();
        },
        [refreshServiceRequests, toast]
    );

    const handleServiceRequestNotificationError = React.useCallback(
        (error: any) => {
            log.error('serviceRequest.subscription.failed', error);

            if (!hasSubscriptionErrorRef.current) {
                toast({
                    title: 'Live-Updates unterbrochen. Aktualisierung erfolgt im Hintergrund.',
                    status: 'warning',
                    duration: 4000,
                });
            }

            hasSubscriptionErrorRef.current = true;
        },
        [toast]
    );

    useSubscription(onServiceRequestNotification, {
        onData: handleServiceRequestNotification,
        onError: handleServiceRequestNotificationError,
    });

    React.useEffect(() => {
        const interval = window.setInterval(() => {
            refreshServiceRequests();
        }, 60000);

        return () => {
            window.clearInterval(interval);
        };
    }, [refreshServiceRequests]);

    React.useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshServiceRequests();
            }
        };

        const handleFocus = () => refreshServiceRequests();

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [refreshServiceRequests]);

    const openServiceRequests = data?.openServiceRequests
        ?.serviceRequests as ServiceRequestFieldsFragment[];
    const pageSize = 5;
    const { pageCount } = data?.openServiceRequests?.pageInfo ?? {};

    const branches = data?.openServiceRequests?.branches || [];
    const sourceSystems = data?.openServiceRequests?.sourceSystems || [];

    const { fetchData } = useDataLoader(load);
    const fetchDataWithFilters = useFetchDataWithFilter(fetchData, { ...variables, filter });

    async function approveServiceRequest(
        serviceRequest: ServiceRequestFieldsFragment,
        redirect: boolean = false
    ) {
        const response = await approveServiceRequestMutation({
            variables: {
                input: {
                    serviceRequestId: serviceRequest.id,
                    userId: viewer.app?.mitarbeiterId,
                    redirect,
                },
            },
        });
        log.debug('approveServiceRequest response', response);
        const result = response.data?.approveServiceRequest;

        if (result?.error) {
            return toast({
                title: result?.error.message,
                status: 'error',
                duration: 2000,
            });
        }

        setServiceRequestId(serviceRequest.id);
        toast({
            title: 'Auftrag angenommen, Projekt wird erstellt...',
            status: 'success',
            duration: 2000,
        });
    }

    async function rejectServiceRequest(serviceRequest: ServiceRequestFieldsFragment) {
        const response = await rejectServiceRequestMutation({
            variables: {
                input: {
                    serviceRequestId: serviceRequest.id,
                    userId: viewer.app?.mitarbeiterId,
                },
            },
        });
        log.debug('rejectServiceRequest response', response);
        const result = response.data?.rejectServiceRequest;

        if (result?.error) {
            return toast({
                title: result?.error.message,
                status: 'error',
                duration: 2000,
            });
        }

        setServiceRequestId(serviceRequest.id);
        toast({
            title: 'Auftrag abgelehnt, Projekt wird erstellt...',
            status: 'info',
            duration: 2000,
        });
    }

    // ===============================================
    // CONTROLS
    // ===============================================

    // UCPWO-423 — ein Folgeauftrag, dessen erster Auftrag noch offen ist, bekommt kein
    // Menü: erst der erste wird angenommen oder abgelehnt.
    const enabled = (row?: any) =>
        (canCreate || canEdit || canDelete) && !row?.original?.followUp?.blocked;

    const controls: any = React.useMemo(
        () => [
            {
                title: 'Auftrag annehmen',
                props: {
                    onClick: (row: any) => approveServiceRequest(row.original),
                },
                enabled,
            },
            {
                title: 'Auftrag annehmen & Öffnen',
                props: {
                    onClick: (row: any) => approveServiceRequest(row.original, true),
                },
                enabled,
            },
            {
                title: 'Auftrag ablehnen',
                props: {
                    onClick: (row: any) => rejectServiceRequest(row.original),
                },
                enabled,
            },
        ],
        [toast, enabled]
    );

    // ===============================================
    // TABLE
    // ===============================================

    const columns = React.useMemo(
        () => withRenderers(tables.serviceRequest.columns as any, controls),
        [controls]
    );
    const hiddenColumns = tables?.serviceRequest?.hiddenColumns;

    return {
        serviceRequests: {
            data: openServiceRequests,
            fetchData: fetchDataWithFilters,
            pageSize,
            pageCount,
            loading,
            columns,
            hiddenColumns,
        },
        branches,
        selectBranch,
        sourceSystems,
        selectSourceSystem,
    };
};
